import type { TourActionContext, TourDefinition } from './types';
import { getRouter } from '@/router/instance';
import { useVaultStore } from '@/stores/vaultStore';
import { clickAnimated, clickThrough, delay, tableSettledEmpty } from '@/utils/tour/actions';
import { logger } from '@/utils/logger';
import { waitForCondition, waitForTourElement } from '@/utils/tour/waitForElement';

/**
 * Scripted playback demo for the File Vault: where documents live, how to
 * bring more in, how search and folders keep them findable, and what each
 * row can do. The demo only navigates (stepping into the first folder) and
 * points — it never uploads, renames, moves, or deletes anything.
 */

/**
 * Step into the first folder in the listing. The vault opens folders on a
 * DOUBLE click of the row (single clicks select). Vaults without folders skip
 * the hop quietly.
 */
async function openFirstFolder(ctx: TourActionContext): Promise<void> {
  const list = await waitForTourElement('vault.list', 3000);
  if (!list || ctx.isCancelled()) {
    return;
  }
  const name = await waitForCondition(
    () =>
      list.querySelector<HTMLElement>('.tree-node-content--directory .file-name-clickable') ??
      (tableSettledEmpty(list) ? 'empty' : null),
    3000
  );
  if (!name || name === 'empty' || ctx.isCancelled()) {
    logger.warn('[tour] no folders in the vault — skipping the folder hop');
    return;
  }
  // The list is a CSS-grid table now, so a row is `.row`, not a `<tr>`.
  const row = name.closest<HTMLElement>('.row');
  if (!row) {
    return;
  }
  row.scrollIntoView({ block: 'center', behavior: ctx.animate ? 'smooth' : 'auto' });
  ctx.setHighlight(row);
  if (ctx.animate) {
    await delay(700);
  }
  row.dispatchEvent(new MouseEvent('dblclick', { bubbles: true, cancelable: true, view: window }));
  // Entering the folder grows the page's breadcrumb trail past the vault crumb.
  await waitForCondition(() => {
    const crumbs = document.querySelector('[data-tour="layout.breadcrumb"]');
    return (crumbs?.querySelectorAll('.n-breadcrumb-item').length ?? 0) > 1 ? true : null;
  }, 3000);
  if (ctx.animate) {
    await delay(400);
  }
}

/**
 * The demo file row: prefer a file the store knows has more than one
 * version (so the versions view has something to show), else the first
 * file in the listing.
 */
function findDemoFileRow(list: HTMLElement): HTMLElement | null {
  const contents = [...list.querySelectorAll<HTMLElement>('.tree-node-content--file')];
  const multiVersionName = useVaultStore().items.find(
    item => item.type === 'file' && (item.versionNumber ?? 1) > 1
  )?.name;
  const preferred = multiVersionName
    ? contents.find(c => c.textContent.includes(multiVersionName))
    : undefined;
  return preferred ?? contents.at(0) ?? null;
}

/**
 * Stamp the demo file row's actions cell so the row-actions scene can
 * spotlight one row's toolkit. Transient runtime anchor (any re-render
 * clears it); without a file row the scene just skips.
 */
async function stampRowActions(): Promise<void> {
  const list = document.querySelector<HTMLElement>('[data-tour="vault.list"]');
  if (!list) {
    return;
  }
  for (const stale of document.querySelectorAll('[data-tour="vault.row-actions"]')) {
    stale.removeAttribute('data-tour');
  }
  await waitForCondition(() => list.querySelector('.tree-node-content--file'), 3000);
  const cell = findDemoFileRow(list)?.closest('.row')?.querySelector<HTMLElement>('.actions-cell');
  if (cell) {
    cell.dataset.tour = 'vault.row-actions';
  }
}

/**
 * Open the row's "⋯" menu (the FIRST button in the actions cell — inline
 * action icons follow it) and stamp the teleported dropdown for the next
 * scene. Never clicks the inline icons: those trigger real actions like
 * download.
 */
async function openRowActionsMenu(ctx: TourActionContext): Promise<void> {
  const cell = await waitForTourElement('vault.row-actions', 3000);
  const dots = cell?.querySelector<HTMLElement>('button');
  if (!cell || !dots || ctx.isCancelled()) {
    return;
  }
  for (const stale of document.querySelectorAll('[data-tour="vault.actions-menu"]')) {
    stale.removeAttribute('data-tour');
  }
  await clickAnimated(dots, ctx);
  const menu = await waitForCondition(
    () => document.querySelector<HTMLElement>('.n-dropdown-menu'),
    3000
  );
  if (!menu || ctx.isCancelled()) {
    return;
  }
  menu.dataset.tour = 'vault.actions-menu';
}

/** Close a floating dropdown menu by clicking outside it. */
async function closeActionsMenu(): Promise<void> {
  if (document.querySelector('.n-dropdown-menu')) {
    clickThrough(document.body);
    await delay(300);
  }
}

// Whether the demo actually navigated to the versions page — set
// synchronously by openVersionHistory, so the versions scenes can skip
// instantly when there was no file (or no menu option) to walk into. The
// route check covers resumes that land directly on the versions page.
let versionsOpened = false;

const versionsOpen = (): boolean =>
  versionsOpened || getRouter().currentRoute.value.name === 'vault-file-versions';

/** A settled "is there any file to demo on?" check for the vault listing. */
const hasFileRow = (): boolean => document.querySelector('.tree-node-content--file') !== null;

/**
 * From the open "⋯" menu, choose "Version history" — a pure navigation to
 * the file's versions page. If the option is missing, the menu is closed
 * and the versions scenes skip.
 */
async function openVersionHistory(ctx: TourActionContext): Promise<void> {
  versionsOpened = false;
  const menu = document.querySelector<HTMLElement>('[data-tour="vault.actions-menu"]');
  if (!menu || ctx.isCancelled()) {
    return;
  }
  // Naive puts the select handler on the option BODY, not the option
  // wrapper — synthetic clicks on the wrapper never select anything.
  const option = [...menu.querySelectorAll<HTMLElement>('.n-dropdown-option-body')].find(o =>
    /version history/i.test(o.textContent)
  );
  if (!option) {
    logger.warn('[tour] no "Version history" option in the row menu — skipping versions');
    await closeActionsMenu();
    return;
  }
  ctx.setHighlight(option);
  if (ctx.animate) {
    await delay(600);
  }
  await clickAnimated(option, ctx);
  versionsOpened = !ctx.isCancelled();
  await waitForCondition(() => document.querySelector('[data-tour="vault.versions-table"]'), 3000);
}

/** Back to the vault listing from the versions page (a :uid route). */
async function goBackInHistory(): Promise<void> {
  getRouter().back();
  await delay(500);
}

/**
 * Open the Compare Versions modal and stamp its card for the next scene.
 * Opening is pure UI state; the actual Compare button inside is never
 * clicked by the tour.
 */
async function openCompareModal(ctx: TourActionContext): Promise<void> {
  const button = await waitForTourElement('vault.compare-button', 3000);
  if (!button || button.hasAttribute('disabled') || ctx.isCancelled()) {
    return;
  }
  for (const stale of document.querySelectorAll('[data-tour="vault.compare-modal"]')) {
    stale.removeAttribute('data-tour');
  }
  await clickAnimated(button, ctx);
  const body = await waitForCondition(
    () => document.querySelector<HTMLElement>('.compare-modal-body'),
    3000
  );
  const card = body?.closest<HTMLElement>('.n-card');
  if (card) {
    card.dataset.tour = 'vault.compare-modal';
  }
}

/** Close the compare modal via its own Cancel button. */
async function closeCompareModal(ctx: TourActionContext): Promise<void> {
  const modal = document.querySelector<HTMLElement>('[data-tour="vault.compare-modal"]');
  if (!modal) {
    return;
  }
  const cancel = [...modal.querySelectorAll<HTMLElement>('button')].find(
    b => b.textContent.trim() === 'Cancel'
  );
  if (!cancel || ctx.isCancelled()) {
    return;
  }
  await clickAnimated(cancel, ctx);
  await delay(300);
}

export const vaultTour: TourDefinition = {
  key: 'file-vault',
  title: 'Contract Vault',
  description: 'Where documents live: folders, search, and file actions.',
  entryRoute: 'file-vault',
  // Land back at the vault root; the demo only navigated, nothing to clean.
  onComplete: async () => {
    try {
      await getRouter().push({ name: 'file-vault' });
    } catch {
      // Navigation declined; completing the tour still stands.
    }
  },
  steps: [
    {
      id: 'list',
      route: 'file-vault',
      target: 'vault.list',
      placement: 'top',
      title: 'Your documents, in one place',
      body:
        'Every file and folder your team stores lives here. The Status ' +
        'column tells you when GAIA has finished reading a document: Done ' +
        'means it is ready for AI features.',
    },
    {
      id: 'new',
      target: 'vault.new-button',
      placement: 'bottom-end',
      title: 'Bringing files in',
      body:
        'Upload brings in files or whole folders, and creates folders to ' +
        'keep things tidy. Dropping files anywhere on the list works too. I ' +
        'will leave the uploading to you.',
    },
    {
      id: 'search',
      target: 'vault.search',
      placement: 'bottom',
      title: 'Find anything, from anywhere',
      body:
        'Search looks across every folder at once, so you never need to ' +
        'remember where something was filed.',
    },
    {
      id: 'folder',
      target: 'vault.list',
      placement: 'top',
      title: 'Folders keep it organized',
      body: 'A folder opens with a click on its name. Let me step into the ' + 'first one.',
      actions: [{ kind: 'custom', run: openFirstFolder }],
    },
    {
      id: 'breadcrumbs',
      target: 'layout.breadcrumb',
      placement: 'bottom',
      title: 'You always know where you are',
      body: 'The trail up here tracks your place. Click any part of it to ' + 'jump straight back.',
    },
    {
      id: 'row-actions',
      prerequisite: hasFileRow,
      onEnter: stampRowActions,
      target: 'vault.row-actions',
      placement: 'left',
      title: 'Every file has quick actions',
      body:
        'Quick actions sit right on the row, and the dots hold the rest. ' +
        'Tick several rows and bulk actions appear above the list. Let me ' +
        'open the menu.',
      actions: [{ kind: 'custom', run: openRowActionsMenu }],
    },
    {
      id: 'actions-menu',
      prerequisite: hasFileRow,
      target: 'vault.actions-menu',
      placement: 'left',
      title: 'Everything you can do with a file',
      body:
        'Preview, download, rename, move, and more. Version history keeps ' +
        'every revision of the file; let me open it.',
      actions: [{ kind: 'custom', run: openVersionHistory }],
      // Back must not leave the menu floating over the previous scene.
      backActions: [{ kind: 'custom', run: closeActionsMenu }],
    },
    {
      id: 'versions',
      prerequisite: versionsOpen,
      target: 'vault.versions-table',
      placement: 'top',
      title: 'Every version of the file',
      body:
        'New uploads of the same file stack here as versions, newest ' +
        'first. Preview or download any of them, or upload the next ' +
        'version.',
      backActions: [{ kind: 'custom', run: goBackInHistory }],
    },
    {
      id: 'compare',
      target: 'vault.compare-button',
      placement: 'bottom-end',
      title: 'What changed between versions?',
      // Needs the versions page (same settled page as the previous scene)
      // with the button enabled — fewer than two versions disable it, and
      // off the versions page it does not exist at all, so the scenes skip
      // instantly rather than pointing at a dead control.
      prerequisite: (): boolean => {
        const button = document.querySelector<HTMLElement>('[data-tour="vault.compare-button"]');
        return (
          button !== null && button.getClientRects().length > 0 && !button.hasAttribute('disabled')
        );
      },
      body: 'Compare answers that side by side. Let me open it.',
      actions: [{ kind: 'custom', run: openCompareModal }],
    },
    {
      id: 'compare-modal',
      prerequisite: versionsOpen,
      target: 'vault.compare-modal',
      placement: 'left',
      title: 'Pick two, see the difference',
      body:
        'Choose the older and the newer version, and Compare lays out ' +
        'exactly what changed between them. I will close it and leave the ' +
        'comparing to you.',
      actions: [{ kind: 'custom', run: closeCompareModal }],
      // Back must not leave the modal over the previous scene.
      backActions: [{ kind: 'custom', run: closeCompareModal }],
    },
    {
      id: 'finale',
      title: 'That is the Contract Vault',
      body:
        'Upload with New or a simple drag in, organize with folders, and ' +
        'search across everything. A file only carries a badge when ' +
        'something needs your attention.',
    },
  ],
};
